import { useQuery } from "@apollo/client";
import { addDays, isPast } from "date-fns";
import React, { useMemo } from "react";

import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { makeStyles } from "@mui/styles";

import { kickLightBlue, kickNavy } from "../styles/portalStyles";

import {
  displayFriendlyLongDate,
  isPharmacyPickupOrderType,
} from "~/shared/utils/sharedUtils";

import {
  groupVisitsByVertical,
  verticalBadgeColorForVisit,
  verticalLabelForVisit,
} from "~/patient-portal/utils/visitGrouping";

import { addressLines } from "~/patient-portal/utils/treatmentCard";

import { medicationLabel } from "~/patient-portal/utils/medicationLabel";

import { GET_PATIENT_RECORD } from "./portalQueries";

import PortalShipmentHistory from "./portalShipmentHistory";

const useStyles = makeStyles((theme) => ({
  content: {
    flexGrow: 1,
    padding: theme.spacing(1),
  },
  page: {
    maxWidth: 860,
    margin: "0 auto",
    padding: "0 16px 64px",
    fontFamily: "BasisGrotesquePro-Regular",
  },
  pageTitle: {
    fontFamily: "BasisGrotesquePro-Bold",
    fontSize: 24,
    color: kickNavy,
    margin: "0 0 12px",
  },
  badges: {
    display: "flex",
    flexWrap: "wrap",
    gap: 6,
    marginBottom: 6,
  },
  badge: {
    display: "inline-block",
    padding: "1px 8px",
    borderRadius: 10,
    background: kickLightBlue,
    color: kickNavy,
    fontSize: 11,
    fontFamily: "BasisGrotesquePro-Bold",
    textTransform: "uppercase",
    letterSpacing: "0.03em",
  },
  table: {
    border: "1px solid #e4e4ee",
    borderRadius: 10,
    overflow: "hidden",
    background: "#fff",
  },
  rxRow: {
    display: "grid",
    gridTemplateColumns: "1.8fr 0.9fr 1.5fr",
    gap: 8,
    padding: "11px 14px",
    fontSize: 14,
    color: "#444",
    alignItems: "start",
  },
  pharmacyMethod: {
    fontFamily: "BasisGrotesquePro-Bold",
    color: kickNavy,
  },
  pharmacyDetail: {
    fontSize: 13,
    color: "#666",
  },
  headerRow: {
    fontFamily: "BasisGrotesquePro-Bold",
    color: kickNavy,
    background: kickLightBlue,
    fontSize: 12.5,
    textTransform: "uppercase",
    letterSpacing: "0.03em",
  },
  zebra: {
    borderTop: "1px solid #eee",
  },
  primaryCell: {
    fontFamily: "BasisGrotesquePro-Bold",
    color: kickNavy,
  },
  expiredTag: {
    color: "#999",
    fontSize: 12,
  },
  // expired tag shown under the Date Written column
  statusUnderDate: {
    marginTop: 4,
  },
  // mobile stacked card
  stackedItem: {
    border: "1px solid #e4e4ee",
    borderRadius: 10,
    padding: "12px 14px",
    marginBottom: 10,
    fontSize: 14,
    color: "#444",
    background: "#fff",
  },
  stackedPrimary: {
    fontFamily: "BasisGrotesquePro-Bold",
    color: kickNavy,
    marginBottom: 2,
  },
  emptyState: {
    textAlign: "center",
    fontSize: 17,
    color: kickNavy,
    margin: "24px 0",
  },
}));

// A prescription expires ~1 year after it was written.
function isRxExpired(rx) {
  return isPast(addDays(new Date(rx.createdAt), 362));
}

// Pharmacy column content for a prescription:
//   - mail order → "Kick Delivery" with no address. Per-shipment delivery
//     addresses live in the Shipment History table, so we don't duplicate one here.
//   - pickup → "Local Pick-up" with the pharmacy name and address from the
//     visit's pharmacyInfo.
// Returns { method, detail: [] }.
export function pharmacyLines(rx, visitsById) {
  const visit = visitsById[rx.visitID];
  if (!visit) return { method: "", detail: [] };

  if (!isPharmacyPickupOrderType(visit.orderType)) {
    return { method: "Kick Delivery", detail: [] };
  }

  const info = visit.pharmacyInfo || {};
  const detail = info.pharmacyName ? [info.pharmacyName] : [];
  detail.push(
    ...addressLines({
      address1: info.pharmacyAddress1,
      address2: info.pharmacyAddress2,
      city: info.pharmacyCity,
      state: info.pharmacyState,
      zip: info.pharmacyZip,
    }),
  );

  return { method: "Local Pick-up", detail };
}

function PharmacyCell({ classes, rx, visitsById }) {
  const { method, detail } = pharmacyLines(rx, visitsById);
  if (!method) return null;
  return (
    <div>
      <div className={classes.pharmacyMethod}>{method}</div>
      {detail.map((line, i) => (
        <div className={classes.pharmacyDetail} key={i}>
          {line}
        </div>
      ))}
    </div>
  );
}

// Status indicator shown under the Date Written column: a subtle "(expired)" tag for a
// prescription written over a year ago. Returns null otherwise so no empty row appears.
function RxStatusBadge({ classes, rx }) {
  if (!isRxExpired(rx)) return null;
  return (
    <div className={classes.statusUnderDate}>
      <span className={classes.expiredTag}>(expired)</span>
    </div>
  );
}

function RxTable({ classes, rows, visitsById, isMobile }) {
  if (isMobile) {
    return (
      <div>
        {rows.map(({ rx, clinic, clinicColor }) => (
          <div className={classes.stackedItem} key={rx.id}>
            {clinic && (
              <div className={classes.badges}>
                <span
                  className={classes.badge}
                  style={{ background: clinicColor }}
                >
                  {clinic}
                </span>
              </div>
            )}
            <div className={classes.stackedPrimary}>
              {medicationLabel(rx, visitsById[rx.visitID]?.orderItems)}
            </div>
            <div style={{ marginTop: 6 }}>
              Date Written {displayFriendlyLongDate(rx.createdAt)}
              <RxStatusBadge classes={classes} rx={rx} />
            </div>
            <div style={{ marginTop: 6 }}>
              <PharmacyCell classes={classes} rx={rx} visitsById={visitsById} />
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className={classes.table}>
      <div className={`${classes.rxRow} ${classes.headerRow}`}>
        <div>Medication</div>
        <div>Date Written</div>
        <div>Pharmacy</div>
      </div>
      {rows.map(({ rx, clinic, clinicColor }, idx) => (
        <div
          className={`${classes.rxRow} ${idx > 0 ? classes.zebra : ""}`}
          key={rx.id}
        >
          <div>
            {clinic && (
              <div className={classes.badges}>
                <span
                  className={classes.badge}
                  style={{ background: clinicColor }}
                >
                  {clinic}
                </span>
              </div>
            )}
            <div className={classes.primaryCell}>
              {medicationLabel(rx, visitsById[rx.visitID]?.orderItems)}
            </div>
          </div>
          <div>
            {displayFriendlyLongDate(rx.createdAt)}
            <RxStatusBadge classes={classes} rx={rx} />
          </div>
          <PharmacyCell classes={classes} rx={rx} visitsById={visitsById} />
        </div>
      ))}
    </div>
  );
}

export default function PortalPrescriptionHistory() {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const { loading, error, data } = useQuery(GET_PATIENT_RECORD);

  const visits = data?.patientRecord?.visits;
  const visitsById = useMemo(() => {
    const map = {};
    (visits || []).forEach((v) => {
      map[v.id] = v;
    });
    return map;
  }, [visits]);

  if (loading) return "Loading...";
  if (error) return `Error! ${error.message}`;

  const { verticals } = groupVisitsByVertical(visits);
  // One combined, newest-first list across all clinics. Each row's clinic badge
  // ("Sleep" / "Performance Anxiety" / "Generalized Anxiety") is derived from its
  // own visit's order_type — so a visit switched between verticals mid-treatment
  // (order_type updated, visit_type left as-is) is labeled by the medication the
  // patient actually ended up on, not the vertical they originally came in for.
  const rxRows = verticals
    .flatMap((v) => v.rxHistoryAll)
    .map((rx) => {
      const visit = visitsById[rx.visitID];
      return {
        rx,
        clinic: verticalLabelForVisit(visit),
        clinicColor: verticalBadgeColorForVisit(visit),
      };
    })
    .sort((a, b) => new Date(b.rx.createdAt) - new Date(a.rx.createdAt));

  return (
    <main className={classes.content}>
      <div className={classes.page}>
        <h3 className={classes.pageTitle}>Prescription History</h3>

        {rxRows.length === 0 ? (
          <p className={classes.emptyState}>
            Your prescription history will appear here.
          </p>
        ) : (
          <RxTable
            classes={classes}
            rows={rxRows}
            visitsById={visitsById}
            isMobile={isMobile}
          />
        )}

        <PortalShipmentHistory visits={visits} isMobile={isMobile} />
      </div>
    </main>
  );
}
